import { Injectable } from '@angular/core';
import {DefaultPermissions} from '../models/defaultPermissions.model';
import {UserService} from '../../../services/user.service';
import {Asset} from '../../../../main/modules/asset/models/asset.model';
import {DataSource} from '../../../../data-source/models/data-source.modol';
import {Dashboard} from '../../../../dashboard/models/dashboard';
import {Workspace} from '../../../models/work-space.model';
import {environment} from '../../../../../../environments/environment';
import {UserModel} from '../../../models/user.model';
import {exhaustMap, filter, map, take} from 'rxjs/operators';
import {Observable} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {AssetsRequest} from '../../../../main/modules/asset/models/assets-request.model';
import {PermissionsMap} from '../models/permissions-map.type';
import {DashboardGroupInfo} from '../../../../main/modules/asset/models/dashboard-group-info.model';
import {DataSourceInDataModelling} from '../../../../data-modelling/models/dataSourceInDataModelling';
import {Relation} from '../../../../data-modelling/models/relation';
import {ChartModel} from '../../../../dashboard/models/chart';
import {SortOrder} from '../../../models/sort.order';
import { PaginatedUserWorkspacesRequest } from '../../../../main/modules/asset/models/paginated-user-workspaces-request.model';
import { NewestDashboardUserAccess } from '../../../../main/modules/asset/models/newest-dashboard-user-access-request.model';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private api = environment.api;
  private allWorkspaceId = environment.allWorkspaceId;
  private defaultPermissions: DefaultPermissions;
  private userObservable: Observable<UserModel>;

  constructor(private userService: UserService,private http: HttpClient) {
    this.defaultPermissions = new DefaultPermissions();
    this.userObservable = this.userService.getUserMinState().pipe(filter(user => user !== null),take(1));
  }

  loadUserWorkspaces$(sortOrder: SortOrder): Observable<Partial<Workspace>[]> {
    return this.userObservable.pipe(exhaustMap(user =>
      this.getUserWorkSpaces(sortOrder).pipe(map(res => this.addWorkspacesPermissions(res.data,user)))));
  }

  getUserWorkSpaces(sortOrder: SortOrder): Observable<any> {
    return this.http.post(`${this.api}/Workspace/GetUserWorkspaces`, {sortOrder});
  }

  loadHomeMostUsedAssets$(body: any): Observable<Asset[]> {
    return this.userObservable.pipe(exhaustMap(user =>
      this.getHomeMostUsedAssets(body).pipe(map(res => this.addAssetsPermissions(res.data,user)))));
  }

  getHomeMostUsedAssets(body: any): Observable<any> {
    return this.http.post(`${this.api}/View/GetMostUsedView`,body);
  }

  loadHomeBookmarkedAssets$(body: any): Observable<Asset[]> {
    return this.userObservable.pipe(exhaustMap(user =>
      this.getHomeBookmarkedAssets(body).pipe(map(res => this.addAssetsPermissions(res.data,user)))));
  }

  getHomeBookmarkedAssets(body: any): Observable<any> {
    return this.http.post(`${this.api}/Bookmark/GetBookmarks`,body);
  }

  loadHomeRecentAssets$(body: any): Observable<Asset[]> {
    return this.userObservable.pipe(exhaustMap(user =>
      this.getHomeRecentAssets(body).pipe(map(res => this.addAssetsPermissions(res.data,user)))));
  }

  getHomeRecentAssets(body: any): Observable<any> {
    return this.http.post(`${this.api}/View/GetRecentView`,body);
  }

  loadHomeDashboardGroup$(body: any): Observable<Asset[]> {
    return this.userObservable.pipe(exhaustMap(user =>
      this.getHomeDashboardGroup(body).pipe(map(res => this.addAssetsPermissions(res.data, user)))));
  }

  getHomeDashboardGroup(body: any): Observable<any> {
    return this.http.post(`${this.api}/DashboardGroup/GetHomeDashboardGroup`, body);
  }

  loadNewestDashboardUserAccess$(loadingNewestDashboardUserAccess: NewestDashboardUserAccess): Observable<Asset[]> {
    return this.userObservable.pipe(exhaustMap(user =>
      this.getNewestDashboardUserAccess(loadingNewestDashboardUserAccess).pipe(map(res => this.addAssetsPermissions(res.data, user)))));
  }

  getNewestDashboardUserAccess(body: any): Observable<any> {
    return this.http.post(`${this.api}/Access/GetNewestDashboardUserAccess`, body);
  }

  getPaginatedUserWorkspaces(body: any): Observable<any> {
    return this.http.post(`${this.api}/Workspace/GetPaginatedUserWorkspaces`, body);
  }

  loadPaginatedUserWorkspaces$(loadingPaginatedUserWorkspacess: PaginatedUserWorkspacesRequest): Observable<Partial<Workspace>[]> {
    return this.userObservable.pipe(exhaustMap(user =>
    this.getPaginatedUserWorkspaces(loadingPaginatedUserWorkspacess).pipe(map(res =>
      this.addWorkspacesPermissions(res.data,user)))));
  }

  loadAllAssets$(loadingAssets: AssetsRequest): Observable<{assets: Asset[];totalPages: number}> {
    return this.userObservable.pipe(exhaustMap(user =>
    this.getAllAssets(loadingAssets).pipe(map(res =>
      ({assets: this.addAssetsPermissions(res.data.pageData, user),totalPages: res.data.totalPages})))));
  }

  getAllAssets(body: any): Observable<any>  {
    return this.http.post(`${this.api}/Asset/GetAllAssetExplore`,body);
  }

  loadDashboardAssets$(loadingAssets: AssetsRequest): Observable<{assets: Asset[];totalPages: number}> {
    return this.userObservable.pipe(exhaustMap(user =>
      this.getDashboardAssets(loadingAssets).pipe(map(res =>
        ({assets: this.addAssetsPermissions(res.data.pageData, user),totalPages: res.data.totalPages})))));
  }

  getDashboardAssets(body: AssetsRequest): Observable<any> {
    return this.http.post(`${this.api}/Asset/GetDashboardAssetExplore`,body);
  }

  loadDataSourceAssets$(loadingAssets: AssetsRequest): Observable<{assets: Asset[];totalPages: number}> {
    return this.userObservable.pipe(exhaustMap(user =>
      this.getDatasourceAssets(loadingAssets).pipe(map(res =>
        ({assets: this.addAssetsPermissions(res.data.pageData, user),totalPages: res.data.totalPages})))));
  }

  getDatasourceAssets(body: AssetsRequest): Observable<any> {
    return this.http.post(`${this.api}/Asset/GetDataSourceAssetExplore`,body);
  }

  getDataSourceExplore(body: AssetsRequest): Observable<any> {
    return this.http.post(`${this.api}/Asset/GetDataSourceExplore`, body);
  }

  loadDashboardGroupAssets$(loadingAssets: AssetsRequest): Observable<{assets: Asset[];totalPages: number}> {
    return this.userObservable.pipe(exhaustMap(user =>
      this.getDashboardGroupAssets(loadingAssets).pipe(map(res =>
        ({assets: this.addAssetsPermissions(res.data.pageData, user),totalPages: res.data.totalPages})))));
  }

  getDashboardGroupAssets(body: AssetsRequest): Observable<any> {
    return  this.http.post(`${this.api}/Asset/GetDashboardGroupAssetExplore`,body);
  }

  loadDashboardsForAddToGroup(workspaceId: string, dashboardGroupId: string): Observable<Asset[]> {
    return this.userObservable.pipe(exhaustMap(user =>
      this.getDashboardsForAddToGroup(workspaceId,dashboardGroupId).
      pipe(map(res => this.addAssetsPermissions(res.data,user)))));
  }

  getDashboardsForAddToGroup(workspaceId: string, dashboardGroupId: string): Observable<any> {
    const bodyRequest = {
      workspaceId,
      dashboardGroupId,
    };
    return this.http.post(`${this.api}/Dashboard/GetDashboardsForAddToGroups`,bodyRequest);
  }

  loadDataSource(workspaceId: string, dataSourceId: string): Observable<DataSource> {
    return this.userObservable.pipe(exhaustMap(user =>
    this.getMainDataSource(workspaceId,dataSourceId).
    pipe(map(res => this.addDataSourcePermissions(res.data as DataSource,user) as DataSource))));
  }


  loadAllRelations(workspaceId: string): Observable<{dataSources: DataSourceInDataModelling[];relations: Relation[]}> {
    return this.userObservable.pipe(exhaustMap(user =>
      this.getAllRelations(workspaceId).pipe(map(res =>
        this.addDataSourcesModellingPermissions(res.data.dataSources,res.data.relations, user)))));
  }

  getAllRelations(workspaceId: string): Observable<any>{
    return this.http.post(this.api + '/Relation/GetAllDataSourceRelations',{workspaceId});
  }

  getMainDataSource(workspaceId: string, dataSourceId: string): Observable<any> {
    const bodyRequest = {
      workspaceId,
      dataSourceId,
    };
    return this.http.post<any>(this.api + '/DataSource/GetDataSource', bodyRequest);
  }

  loadSelectedDashboardGroup$(workspaceId: string, dashboardGroupId: string): Observable<DashboardGroupInfo> {
    return this.userObservable.pipe(exhaustMap(user =>
      this.getDashboardGroup(workspaceId,dashboardGroupId).
      pipe(map(res => this.addDashboardGroupPermissions(res.data as DashboardGroupInfo,user)))));
  }

  getDashboardGroup(workspaceId: string, dashboardGroupId: string): Observable<any> {
    const bodyRequest = {
      workspaceId,
      dashboardGroupId,
    };
    return this.http.post<any>(`${this.api}/DashboardGroup/GetDashboardGroup`, bodyRequest);
  }

  getSelectedWorkspacePermissions(selectedWorkspaceId: string, user: UserModel): number[] {
    return !user.isSuperAdmin ?  user?.workspacesHighestLevel?.[selectedWorkspaceId]?.permissions : null;
  }

  getPermissionsOnEachChartInDetail(workspaceId: string, dashboardId: string): Observable<any> {
    const bodyRequest = {
      workspaceId,
      dashboardId,
    };
    return this.http.post(`${this.api}/Chart/GetPermissionsOnEachChartInDetail`, bodyRequest);
  }

  checkAssetPermissions(permissions: PermissionsMap,
                        type: string,isSuperAdmin: boolean): boolean {
    if(type === 'Dashboard') {
      return !permissions.get('Edit').isChecked &&
        !permissions.get('Permissions').isChecked &&
        !permissions.get('Activate/Deactivate').isChecked &&
        !permissions.get('Remove').isChecked &&
        !isSuperAdmin;
    }
    if(type === 'DataSource') {
      return !permissions.get('Edit').isChecked &&
        !permissions.get('Permissions').isChecked &&
        !permissions.get('Remove').isChecked &&
        !permissions.get('ViewHistoryChange').isChecked &&
        !permissions.get('Remove').isChecked &&
        !permissions.get('CreateDashboard').isChecked &&
        !isSuperAdmin;
    }
    if(type === 'DashboardGroup') {
      return !permissions.get('Edit').isChecked &&
        !permissions.get('Permissions').isChecked &&
        !permissions.get('Remove').isChecked &&
        !isSuperAdmin;
    }
    return false;
  }

  private getMapPermissions(permissions: number[] | null,
                    permissionGroupType: string, isSuperAdmin: boolean): Map<string,{id: string;isChecked: boolean}> {
    return this.defaultPermissions.mapSpecificPermissions(permissions,permissionGroupType,false,isSuperAdmin);
  }

  private isValidSelectedWorkspaceId(selectedWorkspaceId: string): boolean {
    return this.allWorkspaceId !== selectedWorkspaceId && selectedWorkspaceId !== null;
  }

  private addWorkspacePermissions(workspace: Partial<Workspace>,user: UserModel): Partial<Workspace> {
    return this.addWorkspacesPermissions([workspace],user)[0];
  }

  private addDashboardGroupPermissions(dashboardGroupInfo: DashboardGroupInfo, user: UserModel): DashboardGroupInfo {
    dashboardGroupInfo.permissions = this.getMapPermissions
    (dashboardGroupInfo.permissions as number[], 'Dashboard',user.isSuperAdmin);
    return dashboardGroupInfo;
  }

  private addAssetsPermissions(assets: Asset[],user: UserModel): Asset[] {
    const result: Asset[] = [];
    assets.forEach(asset => {
      if(asset.assetType === 'DataSource') {
        asset = this.addDataSourcePermissions(asset,user) as Asset;
      } else if(asset.assetType === 'Dashboard') {
        asset = this.addDashboardPermissions(asset,user) as Asset;
      } else {
        asset.permissions = this.getMapPermissions(asset.permissions as number[],asset.assetType,user.isSuperAdmin);
      }
      asset.noPermissionExist = this.checkAssetPermissions(asset.permissions as PermissionsMap,
        asset.assetType,user.isSuperAdmin);
      result.push(asset);
    });
    return result;
  }

  private addDashboardPermissions(dashboard: Asset, user: UserModel): Dashboard | Asset{
    const permissions = dashboard.permissions as number[];
    dashboard.noPermissionExist =
      (permissions === null || permissions?.length === 0) && !user.isSuperAdmin;
    dashboard.permissions = this.getMapPermissions(dashboard.permissions as number[], 'Dashboard',user.isSuperAdmin);
    return dashboard;
  }

  private addWorkspacesPermissions(workspaces: Partial<Workspace>[], user: UserModel): Partial<Workspace>[] {
    const result: Partial<Workspace>[] = [];
      const allWorkspaces: Partial<Workspace> = {
        workspaceId: this.allWorkspaceId,
        name: 'همه فضاهای کاری',
        imagePath: '',
        roleId: '00000000-0000-0000-0000-000000000000',
        roleName: 'سوپر ادمین',
        inActive: false,
      };
      workspaces.push(allWorkspaces);

    workspaces.forEach(workspace => {
      if(this.isValidSelectedWorkspaceId(workspace.workspaceId)) {
        const permissions = this.getSelectedWorkspacePermissions(workspace.workspaceId,user);
        // console.log(workspace.name,permissions);
        // workspace.noPermissionExist = (permissions === null || permissions?.length === 0) && !user.isSuperAdmin;
        workspace.permissions = this.getMapPermissions(permissions,'Workspace',user.isSuperAdmin);
        if(workspace.workspaceId === "0c2d7a7a-1e90-41c8-a481-b18200e4051a"){
        }
      } else {
        workspace.permissions = this.getMapPermissions(null,'Workspace',user.isSuperAdmin);
      }
      result.push(workspace);
    });
    return result;
  }

  private addDataSourcePermissions(dataSource: DataSource | Asset,user: UserModel): DataSource | Asset {
    const workspacePermission = this.getMapPermissions(
      this.getSelectedWorkspacePermissions(dataSource.workspaceId,user),
      'Workspace',user.isSuperAdmin);
    const permissions = dataSource.permissions as number[];
    dataSource.noPermissionExist = (permissions === null || permissions?.length === 0) &&
      !user.isSuperAdmin;
    dataSource.permissions = this.getMapPermissions(permissions, 'DataSource',user.isSuperAdmin);
    dataSource.permissions.set('CreateDashboard',workspacePermission.get('CreateDashboard'));

    return dataSource;
  }

  private addDataSourcesModellingPermissions(dataSources: DataSourceInDataModelling[],
                                    relations: Relation[], user: UserModel): any {
    const filteredDataSources = [];
    const filteredRelations = [];
    dataSources.forEach(dataSource => {
      dataSource.permissions = this.getMapPermissions(dataSource.permissions as number[],
        'DataSource',user.isSuperAdmin);
      dataSource.columns = dataSource.permissions.get('View').isChecked ? dataSource.columns : [];
      filteredDataSources.push(dataSource);
    });
    relations.forEach(relation => {
      const foundedSourceDataSourceId = filteredDataSources.
      findIndex(datasource => datasource.dataSourceId === relation.sourceDataSourceId);
      const foundedDestinationDataSourceId = filteredDataSources.
      findIndex(datasource => datasource.dataSourceId === relation.destinationDataSourceId);
      if(foundedDestinationDataSourceId > -1 && foundedSourceDataSourceId > -1) {
        relation.hasAccess = filteredDataSources[foundedSourceDataSourceId].permissions.get('Relations').isChecked &&
          filteredDataSources[foundedDestinationDataSourceId].permissions.get('Relations').isChecked;
      }
      filteredRelations.push(relation);
    });
    return {dataSources: filteredDataSources, relations: filteredRelations};
  }
}
